import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { CustomerService } from '../../services/customer.service';

@Component({
	selector: 'app-move-transaction',
	templateUrl: './move-transaction.component.html',
	styleUrls: ['./move-transaction.component.scss']
})
export class MoveTransactionComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();
	private transactionsSub: any = null;
	private customerSearchSub: any = null;

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	color: string;
	fromcustomer = new FormControl('');
	tocustomer = new FormControl('');
	filterControl = new FormControl('');
	typeFilterControl = new FormControl<number[]>([]);

	sending = false;
	fromresults: any[] = [];
	toresults: any[] = [];
	from: any = null;
	to: any = null;
	transactions: any = null;
	dataSource = new MatTableDataSource<any>([]);
	selected: any[] = [];

	displayedColumns: string[] = [
		'select',
		'transno',
		'typename',
		'trandate',
		'duedate',
		'order_',
		'invtext',
		'enteredby',
		'settled',
		'totalamount',
		'allocated',
		'balance'
	];

	typeFilters = [
		{ value: 10, name: 'Invoice' },
		{ value: 11, name: 'Credit' },
		{ value: 12, name: 'Payment' }
	];

	constructor(
		private location: Location,
		public router: Router,
		private globalSearchService: GlobalSearchService,
		public customerService: CustomerService
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.dataSource.filterPredicate = (row: any, filter: string) => {
			const text = filter.toLowerCase();
			const parts = [
				row.order_,
				row.transno,
				row.typename,
				row.invtext,
				row.enteredby,
				row.settled,
				row.totalamount,
				row.allocated,
				row.balance,
				row.trandate,
				row.duedate
			].filter(Boolean).map((x) => String(x).toLowerCase());
			return parts.some((p) => p.indexOf(text) !== -1);
		};

		this.filterControl.valueChanges
			.pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe((value: string) => this.applyFilter(value || ''));

		this.typeFilterControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.applyTypeFilter());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.unsubscribeTransactions();
		if (this.customerSearchSub) {
			this.customerSearchSub.unsubscribe();
			this.customerSearchSub = null;
		}
	}

	private unsubscribeTransactions(): void {
		if (this.transactionsSub) {
			this.transactionsSub.unsubscribe();
			this.transactionsSub = null;
		}
	}

	back(): void {
		this.location.back();
	}

	setTo(value: any): void {
		this.to = value;
	}

	setFrom(value: any): void {
		this.from = value;
		this.selected = [];
		this.transactions = null;
		this.dataSource.data = [];
		this.filterControl.setValue('', { emitEvent: false });
		this.typeFilterControl.setValue([], { emitEvent: false });

		this.unsubscribeTransactions();
		this.transactionsSub = this.customerService
			.getDebtorTransactions(value.debtorno)
			.pipe(takeUntil(this.destroy$))
			.subscribe((r: any) => {
				this.transactions = r;
				const list = (r && r.transactions) ? r.transactions : [];
				this.dataSource.data = list;
				this.dataSource.sort = this.sort;
				this.dataSource.paginator = this.paginator;
				this.applyTypeFilter();
			});
	}

	applyFilter(value: string): void {
		this.dataSource.filter = value.trim().toLowerCase();
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	applyTypeFilter(): void {
		const selected = this.typeFilterControl.value;
		if (!this.transactions || !Array.isArray(this.transactions.transactions)) {
			return;
		}
		if (!selected || selected.length === 0) {
			this.dataSource.data = [...this.transactions.transactions];
		} else {
			const set = new Set(selected);
			this.dataSource.data = this.transactions.transactions.filter((t: any) =>
				set.has(Number(t.type))
			);
		}
		this.dataSource._updateChangeSubscription();
		if (this.dataSource.paginator) {
			this.dataSource.paginator.firstPage();
		}
	}

	isSelected(t: any): boolean {
		return this.selected.some((s) => s === t || (s.type === t.type && s.transno === t.transno && s.typeno === t.typeno));
	}

	toggleSelection(t: any): void {
		const idx = this.selected.findIndex(
			(s) => s === t || (s.type === t.type && s.transno === t.transno && s.typeno === t.typeno)
		);
		if (idx >= 0) {
			this.selected.splice(idx, 1);
		} else {
			this.selected.push(t);
		}
	}

	checkAll(event: any): void {
		const list = this.dataSource.filteredData;
		if (event.target.checked) {
			const existing = new Set(
				this.selected.map((s) => `${s.type}-${s.transno}-${s.typeno}`)
			);
			list.forEach((t) => {
				const key = `${t.type}-${t.transno}-${t.typeno}`;
				if (!existing.has(key)) {
					this.selected.push(t);
					existing.add(key);
				}
			});
		} else {
			const toRemove = new Set(list.map((t) => `${t.type}-${t.transno}-${t.typeno}`));
			this.selected = this.selected.filter(
				(s) => !toRemove.has(`${s.type}-${s.transno}-${s.typeno}`)
			);
		}
	}

	isAllChecked(): boolean {
		const list = this.dataSource.filteredData;
		if (!list.length) return false;
		return list.every((t) => this.isSelected(t));
	}

	moveTransactions(): void {
		if (this.selected.length === 0) return;
		if (!confirm('Move selected transactions to the target customer? This will NOT remove allocations.')) {
			return;
		}
		const data = {
			from: this.from,
			to: this.to,
			transactions: this.selected
		};
		this.customerService.moveDebtorTransactions(data).subscribe((r: any) => {
			if (r.success) {
				this.globalSearchService.showNotification('Transactions moved', 'success', 'bottom', 'right');
				this.unsubscribeTransactions();
				this.transactionsSub = this.customerService
					.getDebtorTransactions(this.from.debtorno)
					.pipe(takeUntil(this.destroy$))
					.subscribe((ar: any) => {
						this.transactions = ar;
						const list = (ar && ar.transactions) ? ar.transactions : [];
						this.dataSource.data = list;
						this.dataSource._updateChangeSubscription();
						this.selected = [];
					});
			}
		});
	}

	customerSearch(keyword: string, type: 'from' | 'to'): void {
		if (this.customerSearchSub) {
			this.customerSearchSub.unsubscribe();
		}
		this.sending = true;
		this.customerSearchSub = this.customerService
			.getCustomerSearch({ keywords: keyword })
			.pipe(takeUntil(this.destroy$))
			.subscribe((results: any) => {
				this.sending = false;
				if (type === 'from') {
					this.fromresults = Array.isArray(results) ? results : [];
				} else {
					this.toresults = Array.isArray(results) ? results : [];
				}
			});
	}

	displayFn(customer: any): string {
		return customer ? `${customer.debtorno} ${customer.name || ''}`.trim() : '';
	}
}
